import { usePostHog } from '@posthog/react'
import { type AnyAction, type ThunkDispatch } from '@reduxjs/toolkit'
import { useCallback, useSyncExternalStore } from 'react'
import { useDispatch } from 'react-redux'

import { type RootState } from '../../store/store'
import { hasBeenAsked, markAsked, subscribeToAsked } from './feedbackPromptStorage'
import { FEEDBACK_PROMPT_EVENTS, requestFeedbackPrompt } from './requestFeedbackPrompt'

export type FeedbackRoute = 'review' | 'contact'

export interface UseFeedbackPromptResult {
  hasAnswered: boolean
  requestPrompt: () => void
  answer: (route?: FeedbackRoute) => void
}

const useFeedbackPrompt = (): UseFeedbackPromptResult => {
  const posthog = usePostHog()
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>()
  const hasAnswered = useSyncExternalStore(subscribeToAsked, hasBeenAsked)

  const requestPrompt = useCallback((): void => {
    dispatch(requestFeedbackPrompt())
  }, [dispatch])

  // Only a chosen route retires the prompt. Closing it or letting it time out
  // leaves the flag unset, so it is offered again after the next publish.
  const answer = useCallback(
    (route?: FeedbackRoute): void => {
      if (route === undefined) {
        posthog?.capture(FEEDBACK_PROMPT_EVENTS.dismissed)
        return
      }

      markAsked()
      posthog?.capture(FEEDBACK_PROMPT_EVENTS.routeChosen, { route })
    },
    [posthog],
  )

  return { hasAnswered, requestPrompt, answer }
}

export default useFeedbackPrompt
